'use client'

import {
  useMemo,
  useEffect,
  useRef,
  memo,
  useDeferredValue,
  Component,
  useState,
} from 'react'
import { RefreshCw } from 'lucide-react'
import { getPackageConfig } from '@/lib/ddayTemplates'
import { logger } from '@/lib/logger'
import { ensureRuntimeConfigWithMapsKey } from '@/src/lib/runtime-config-bootstrap.client'
import type {
  StationeryTemplateData,
  StationeryPreviewComponentProps,
} from '@/types/stationery-invitation'

interface ErrorBoundaryProps {
  children: React.ReactNode
  onReset?: () => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class StationeryErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('Stationery template render error:', error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
    this.props.onReset?.()
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-muted h-full flex items-center justify-center">
          <div className="text-center p-8">
            <p className="text-sm text-muted-foreground mb-3">
              Preview temporarily unavailable
            </p>
            <button
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-primary hover:text-primary/80 bg-primary/10 rounded-md transition-colors cursor-pointer"
            >
              <RefreshCw className="w-3 h-3" />
              Retry
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

interface StationeryPreviewProps {
  templateData: StationeryTemplateData
  refreshKey?: number
  maxWidth?: number
}

type StationeryPreviewComponent =
  React.ComponentType<StationeryPreviewComponentProps>

const ASPECT_RATIOS: Record<string, number> = {
  '5x7': 5 / 7,
  '4x6': 4 / 6,
}

const PREVIEW_BASE_HEIGHT = 600

function getCanvasSize(paperSize: string) {
  const ratio = ASPECT_RATIOS[paperSize] ?? ASPECT_RATIOS['5x7']
  return {
    width: Math.round(PREVIEW_BASE_HEIGHT * ratio),
    height: PREVIEW_BASE_HEIGHT,
  }
}

const PAGE_BTN =
  'px-3 py-1.5 text-sm font-medium rounded-md transition-colors cursor-pointer'
const PAGE_BTN_ACTIVE = 'bg-background text-foreground shadow-sm'
const PAGE_BTN_INACTIVE = 'text-muted-foreground hover:text-foreground'

const SinglePageRenderer = memo(function SinglePageRenderer({
  templateData,
  page,
  PreviewComponent,
}: {
  templateData: StationeryTemplateData
  page: 'front' | 'back'
  PreviewComponent: StationeryPreviewComponent
}) {
  return (
    <StationeryErrorBoundary>
      <PreviewComponent
        templateData={templateData}
        activePage={page}
        mode="preview"
        showBleedLines={false}
        showSafeZone={false}
        zoom={100}
        showPageToggle={false}
      />
    </StationeryErrorBoundary>
  )
})

export default function StationeryPreview({
  templateData,
  refreshKey,
  maxWidth = 380,
}: StationeryPreviewProps) {
  const deferredTemplateData = useDeferredValue(templateData)
  const [activePage, setActivePage] = useState<'front' | 'back'>('front')
  const [PreviewComponent, setPreviewComponent] =
    useState<StationeryPreviewComponent | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const isMountedRef = useRef(true)

  const paperSize = deferredTemplateData.paperSize || '5x7'
  const canvasSize = useMemo(() => getCanvasSize(paperSize), [paperSize])
  const scale = useMemo(
    () => Math.min(1, maxWidth / canvasSize.width),
    [maxWidth, canvasSize.width]
  )

  const containerStyle = useMemo(
    () => ({
      width: canvasSize.width * scale,
      height: canvasSize.height * scale,
      overflow: 'hidden' as const,
    }),
    [canvasSize, scale]
  )

  const innerStyle = useMemo(
    () => ({
      width: canvasSize.width,
      height: canvasSize.height,
      transform: `scale(${scale})`,
      transformOrigin: 'top left' as const,
    }),
    [canvasSize, scale]
  )

  useEffect(() => {
    isMountedRef.current = true
    const load = async () => {
      try {
        const pkg = await import('@kiltechco/dday-templates/stationery')
        const mainPkg = await import('@kiltechco/dday-templates')
        if (!isMountedRef.current) return
        await ensureRuntimeConfigWithMapsKey()
        if (!isMountedRef.current) return
        mainPkg.setPackageConfig(getPackageConfig())
        if (pkg.StationeryPreview) {
          setPreviewComponent(
            () => pkg.StationeryPreview as unknown as StationeryPreviewComponent
          )
          setStatus('ready')
        } else {
          setErrorMessage('StationeryPreview not available')
          setStatus('error')
        }
      } catch (err) {
        if (!isMountedRef.current) return
        logger.error('Stationery preview loading error:', err)
        setErrorMessage(err instanceof Error ? err.message : 'Unknown error')
        setStatus('error')
      }
    }
    load()
    return () => {
      isMountedRef.current = false
      setPreviewComponent(null)
    }
  }, [])

  if (status === 'loading') {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
          <p className="text-sm text-muted-foreground">Loading preview...</p>
        </div>
      </div>
    )
  }

  if (status === 'error' || !PreviewComponent) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <p className="text-sm text-muted-foreground/70">
          {errorMessage ?? 'Preview not available'}
        </p>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-center pb-2">
        <div className="flex rounded-lg bg-muted p-1">
          <button
            type="button"
            onClick={() => setActivePage('front')}
            className={
              activePage === 'front'
                ? `${PAGE_BTN} ${PAGE_BTN_ACTIVE}`
                : `${PAGE_BTN} ${PAGE_BTN_INACTIVE}`
            }
          >
            Front
          </button>
          <button
            type="button"
            onClick={() => setActivePage('back')}
            className={
              activePage === 'back'
                ? `${PAGE_BTN} ${PAGE_BTN_ACTIVE}`
                : `${PAGE_BTN} ${PAGE_BTN_INACTIVE}`
            }
          >
            Back
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 items-start justify-center px-2">
        <div className="rounded-lg bg-white shadow-lg" style={containerStyle}>
          <div style={innerStyle}>
            <SinglePageRenderer
              key={`${activePage}-${refreshKey}`}
              templateData={deferredTemplateData}
              page={activePage}
              PreviewComponent={PreviewComponent}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
